/**
 * semrush.ts — what the CMS knows about Semrush's MCP server: where it is, the spending limit per run, how a reply is
 * read, and what each refusal means in plain words. Pure: no network here (the Worker's lib/semrush.ts does the calls).
 *
 * Facts, from Semrush's MCP page and a real call: one server (streamable HTTP), the key goes in `Authorization: Apikey <key>`,
 * a report reply carries `data` (semicolon-separated table) and `metadata.usage.api_units` (what it cost), and a refusal is
 * an object with `code`, `message` and `retryable`.
 */
export const SEMRUSH_MCP_URL = "https://mcp.semrush.com/v2/mcp";
/** The most one run may spend (the team's rule: capped at 6,000 API units per run). */
export const SEMRUSH_RUN_UNIT_CAP = 6000;
/** Used only when a reply does not say what it cost, so the cap can never be dodged by a missing figure. */
export const SEMRUSH_UNKNOWN_COST_UNITS = 10;

export type SemrushProblem = "key" | "no_units" | "no_subscription" | "rate_limit" | "bad_request" | "no_data" | "capped" | "unreachable" | "unexpected";

export interface SemrushRefusal {
  code: string;
  message: string;
  retryable: boolean;
}

export interface SemrushTable {
  columns: string[];
  rows: Array<Record<string, string>>;
}

export interface SemrushReport extends SemrushTable {
  /** What this report cost, in API units. */
  units: number;
  /** The same report on semrush.com, when Semrush gives one. */
  url: string | null;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** Reads a semicolon-separated table (Semrush's own CSV): first line names the columns. Blank lines are skipped. */
export function parseSemrushTable(data: string): SemrushTable {
  const lines = data.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { columns: [], rows: [] };
  const columns = lines[0]!.split(";").map((c) => c.trim());
  const rows = lines.slice(1).map((line) => {
    const cells = line.split(";");
    const row: Record<string, string> = {};
    columns.forEach((c, i) => (row[c] = (cells[i] ?? "").trim()));
    return row;
  });
  return { columns, rows };
}

/** The JSON a tool call answered with: structured content when present, else the text block. Null when neither is readable. */
export function toolPayload(result: unknown): Record<string, unknown> | null {
  if (!isObject(result)) return null;
  if (isObject(result.structuredContent)) return result.structuredContent;
  const content = result.content;
  if (Array.isArray(content)) {
    for (const block of content) {
      if (isObject(block) && block.type === "text" && typeof block.text === "string") {
        try {
          const parsed: unknown = JSON.parse(block.text);
          if (isObject(parsed)) return parsed;
        } catch {
          /* not JSON: keep looking */
        }
      }
    }
  }
  return null;
}

/** A refusal object (code + message, nothing else useful in it), or null when the payload is data. */
export function asRefusal(payload: Record<string, unknown> | null): SemrushRefusal | null {
  if (!payload || typeof payload.code !== "string" || typeof payload.message !== "string") return null;
  if ("data" in payload || "reports" in payload) return null;
  return { code: payload.code, message: payload.message, retryable: payload.retryable === true };
}

export function problemOf(refusal: SemrushRefusal): SemrushProblem {
  switch (refusal.code) {
    case "no_api_units": return "no_units";
    case "no_subscription": return "no_subscription";
    case "rate_limit": return "rate_limit";
    case "validation_failed": return "bad_request";
    case "no_data": return "no_data";
    default: return "unexpected";
  }
}

/** One plain sentence for each problem. `detail` is Semrush's own one-line reason, shown only where it helps. */
export function semrushMessage(problem: SemrushProblem, detail = ""): string {
  const d = detail.trim().slice(0, 700);
  switch (problem) {
    case "key": return "Semrush did not accept this key. Copy it again from Semrush and replace it.";
    case "no_units": return "The Semrush account has no API units left. The account owner has to add or allocate units in Semrush.";
    case "no_subscription": return "The Semrush plan behind this key does not include API access. Ask the account owner.";
    case "rate_limit": return "Semrush asked us to slow down. Try again in a minute.";
    case "bad_request": return d ? `Semrush did not accept the request: ${d}` : "Semrush did not accept the request.";
    case "no_data": return "Semrush has no data for that.";
    case "capped": return `This run reached its limit of ${SEMRUSH_RUN_UNIT_CAP.toLocaleString("en-US")} Semrush units, so it stopped.`;
    case "unreachable": return "Could not reach Semrush. Try again in a minute.";
    case "unexpected": return d ? `Semrush answered something unexpected: ${d}` : "Semrush answered something unexpected.";
  }
}

/** Reads a finished `execute_report` payload into a table and its cost. Null when it does not look like a report. */
export function parseSemrushReport(payload: Record<string, unknown> | null): SemrushReport | null {
  if (!payload || typeof payload.data !== "string") return null;
  const table = parseSemrushTable(payload.data);
  const meta = isObject(payload.metadata) ? payload.metadata : {};
  const usage = isObject(meta.usage) ? meta.usage : {};
  const units = typeof usage.api_units === "number" && Number.isFinite(usage.api_units) && usage.api_units >= 0 ? usage.api_units : SEMRUSH_UNKNOWN_COST_UNITS;
  return { ...table, units, url: typeof meta.url === "string" ? meta.url : null };
}

/** The report names a discovery call lists, or null when the payload is not a list. */
export function parseReportNames(payload: Record<string, unknown> | null): string[] | null {
  if (!payload || !Array.isArray(payload.reports)) return null;
  return payload.reports.flatMap((r) => (isObject(r) && typeof r.name === "string" ? [r.name] : []));
}
